/**
 * ============================================================
 * uiSlice.ts — Estado de UI (Tema, Dashboard, Filtros, Workflows)
 * ============================================================
 *
 * Responsabilidade: Gerenciar todo o estado relacionado a interface
 *   do usuario: tema (light/dark), filtros de data, configuracao
 *   dos cards do dashboard (visibilidade, ordem, tamanho),
 *   workflows de automacao, campos customizados, membros do time,
 *   e progresso de sincronizacao.
 * Arquitetura: Slice do Zustand que compoe a store global. E o
 *   maior slice da aplicacao, agregando varios dominios de UI
 *   que nao justificam um slice proprio.
 * Padroes: Zustand Slice, Persistencia seletiva (somente tema
 *   persiste no localStorage), Manipulacao direta do DOM para
 *   classe 'dark' no <html> (toggleTheme).
 * Dependencias:
 *   - Depende de: ../types, ../persistence, @/lib/constants, ../helpers
 *   - Quem depende dele: useStore, componentes de dashboard,
 *     header (tema), filtros de data, settings (workflows, campos)
 *
 * ============================================================
 */

import type { StateCreator } from 'zustand';
import type { DashboardCardConfig, DateFilterState, DatePreset, Workflow, CustomFieldDef, TeamMember } from '../types';
import { loadPersisted, persist } from '../persistence';
import { STORAGE_KEYS } from '@/lib/constants';
import { reorderByIndex } from '../helpers';

/**
 * Resolves the effective theme — 'system' looks at the OS preference, the
 * other modes pass through. Tailwind dark-mode uses the 'class' strategy
 * so we toggle the .dark class on <html> directly.
 */
function applyTheme(mode: 'light' | 'dark' | 'system') {
  const isDark = mode === 'dark' || (mode === 'system'
    && typeof window !== 'undefined'
    && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', isDark);
}

// Subscribe to OS color-scheme changes once at module load. When the user has
// theme='system', flipping the OS theme should follow live without a reload.
if (typeof window !== 'undefined' && window.matchMedia) {
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.THEME);
      if (!stored || stored === '"system"' || stored === 'system') {
        applyTheme('system');
      }
    } catch { /* localStorage indisponível: segue o sistema */ }
  };
  mql.addEventListener?.('change', handler);
}

// ── Configuracao Padrao dos Cards do Dashboard ──
// Define todos os cards disponiveis com visibilidade, ordem e tamanho inicial.
// O usuario pode personalizar via drag-and-drop e toggles no painel de config.
const DEFAULT_DASHBOARD_CARDS: DashboardCardConfig[] = [
  { id: 'pipeline-value', label: 'Valor do Pipeline', visible: true, order: 0, size: 'large' },
  { id: 'kpis', label: 'KPIs Principais', visible: true, order: 1, size: 'large' },
  { id: 'charts', label: 'Gráficos Analíticos', visible: true, order: 2, size: 'large' },
  { id: 'funnel', label: 'Funil de Vendas', visible: true, order: 3, size: 'large' },
  { id: 'forecast', label: 'Previsão de Receita', visible: true, order: 4, size: 'large' },
  { id: 'recent-updates', label: 'Últimas Atualizações', visible: true, order: 5, size: 'medium' },
  { id: 'heatmap', label: 'Mapa de Calor Semanal', visible: true, order: 6, size: 'medium' },
  { id: 'ai-alerts', label: 'Alertas Inteligentes', visible: true, order: 7, size: 'medium' },
  { id: 'sdr-ranking', label: 'Ranking SDRs', visible: true, order: 8, size: 'medium' },
  { id: 'closer-ranking', label: 'Ranking Closers', visible: true, order: 9, size: 'medium' },
  { id: 'gerente-overview', label: 'Visão Gerente Comercial', visible: true, order: 10, size: 'large' },
  { id: 'clients', label: 'Carteira de Clientes', visible: true, order: 11, size: 'large' },
  { id: 'source-roi', label: 'ROI por Fonte', visible: true, order: 12, size: 'large' },
  { id: 'win-loss', label: 'Win/Loss Ratio', visible: true, order: 13, size: 'medium' },
  { id: 'cohort', label: 'Análise de Cohort', visible: true, order: 14, size: 'large' },
  { id: 'territory-heatmap', label: 'Mapa de Calor — Localização', visible: true, order: 15, size: 'medium' },
];

// ── Interface do Slice ──
export type ThemeMode = 'light' | 'dark' | 'system';

export interface UISlice {
  /** Stored preference: 'system' follows the OS via prefers-color-scheme. */
  theme: ThemeMode;
  /** Cycles light → dark → system → light. */
  toggleTheme: () => void;
  /** Sets a specific mode (used by 3-state picker). */
  setTheme: (mode: ThemeMode) => void;
  dateFilter: DateFilterState;
  setDatePreset: (preset: DatePreset) => void;
  setCustomDateRange: (from: string, to: string) => void;
  /** 'cohort' = criados no periodo; 'activity' = o que aconteceu no periodo. */
  setDateFilterMode: (mode: 'cohort' | 'activity') => void;
  dashboardCards: DashboardCardConfig[];
  setDashboardCards: (cards: DashboardCardConfig[]) => void;
  toggleDashboardCard: (cardId: string) => void;
  reorderDashboardCards: (fromIndex: number, toIndex: number) => void;
  resizeDashboardCard: (cardId: string, size: 'small' | 'medium' | 'large') => void;
  workflows: Workflow[];
  toggleWorkflow: (id: string) => void;
  customFieldDefs: CustomFieldDef[];
  setCustomFieldDefs: (defs: CustomFieldDef[]) => void;
  addCustomFieldDef: (field: CustomFieldDef) => void;
  deleteCustomFieldDef: (fieldId: string) => void;
  updateCustomFieldDef: (fieldId: string, updates: Partial<CustomFieldDef>) => void;
  teamMembers: TeamMember[];
  setTeamMembers: (members: TeamMember[]) => void;
  syncProgress: number; // 0-100
  setSyncProgress: (p: number) => void;
}

// ── Criacao do Slice ──
export const createUISlice: StateCreator<UISlice, [], [], UISlice> = (set) => ({
  // ── Tema (Light/Dark/System) ──
  // Persistido no localStorage para manter preferencia entre sessoes.
  // 'system' follows window.matchMedia('(prefers-color-scheme: dark)').
  theme: loadPersisted<ThemeMode>(STORAGE_KEYS.THEME, 'system'),
  toggleTheme: () =>
    set((s) => {
      // Cycle: light → dark → system → light
      const next: ThemeMode = s.theme === 'light' ? 'dark' : s.theme === 'dark' ? 'system' : 'light';
      applyTheme(next);
      persist(STORAGE_KEYS.THEME, next);
      return { theme: next };
    }),
  setTheme: (mode) =>
    set(() => {
      applyTheme(mode);
      persist(STORAGE_KEYS.THEME, mode);
      return { theme: mode };
    }),

  // ── Filtro de Data Global ──
  // Usado pelo dashboard e relatorios para definir periodo de analise
  dateFilter: { preset: '30d' as DatePreset, mode: 'activity' }, // Padrao: ultimos 30 dias, por atividade
  setDatePreset: (preset) => set((s) => ({ dateFilter: { preset, mode: s.dateFilter.mode } })),
  // Range customizado: define datas from/to manualmente
  setCustomDateRange: (from, to) => set((s) => ({ dateFilter: { preset: 'custom', customFrom: from, customTo: to, mode: s.dateFilter.mode } })),
  setDateFilterMode: (mode) => set((s) => ({ dateFilter: { ...s.dateFilter, mode } })),

  // ── Cards do Dashboard ──
  // Configuracao de quais cards sao visiveis, sua ordem e tamanho
  dashboardCards: DEFAULT_DASHBOARD_CARDS,
  setDashboardCards: (cards) => set({ dashboardCards: cards }),
  toggleDashboardCard: (cardId) =>
    set((s) => ({
      dashboardCards: s.dashboardCards.map((c) => (c.id === cardId ? { ...c, visible: !c.visible } : c)),
    })),
  // Reordenacao via drag-and-drop (usa helper generico)
  reorderDashboardCards: (fromIndex, toIndex) =>
    set((s) => ({ dashboardCards: reorderByIndex(s.dashboardCards, fromIndex, toIndex) })),
  // Redimensionamento de cards individuais (small/medium/large)
  resizeDashboardCard: (cardId, size) =>
    set((s) => ({
      dashboardCards: s.dashboardCards.map((c) => (c.id === cardId ? { ...c, size } : c)),
    })),

  // ── Workflows de Automacao ──
  // Lista de workflows com toggle de ativacao/desativacao
  workflows: [],
  toggleWorkflow: (id) =>
    set((s) => ({
      workflows: s.workflows.map((w) => (w.id === id ? { ...w, enabled: !w.enabled } : w)),
    })),

  // ── Campos Customizados (Custom Fields) ──
  // Campos definidos pelo usuario para estender o schema de deals
  customFieldDefs: [],
  setCustomFieldDefs: (defs) => set({ customFieldDefs: defs }),
  addCustomFieldDef: (field) => set((s) => ({ customFieldDefs: [...s.customFieldDefs, field] })),
  deleteCustomFieldDef: (fieldId) =>
    set((s) => ({ customFieldDefs: s.customFieldDefs.filter((f) => f.id !== fieldId) })),
  updateCustomFieldDef: (fieldId, updates) =>
    set((s) => ({
      customFieldDefs: s.customFieldDefs.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)),
    })),

  // ── Membros do Time ──
  // Populado via React Query a partir do Supabase
  teamMembers: [],
  setTeamMembers: (members) => set({ teamMembers: members }),

  // ── Progresso de Sincronizacao ──
  // Barra de progresso durante sync inicial (0-100)
  syncProgress: 0,
  setSyncProgress: (p) => set({ syncProgress: p }),
});
